import { Capacitor } from '@capacitor/core';

/**
 * Capacitor keyboard manager for iOS and Android.
 * iOS: Uses @capacitor/keyboard plugin events.
 * Android: Uses visualViewport API (plugin is unreliable on Android).
 * Does NOTHING on web.
 */
export function initCapacitorKeyboard(): void {
  if (!Capacitor.isNativePlatform()) return;

  const platform = Capacitor.getPlatform();

  if (platform === 'ios') {
    initIOSKeyboard();
  } else if (platform === 'android') {
    initAndroidKeyboard();
  }
}

// ── iOS Implementation (unchanged) ─────────────────────────────────────

function initIOSKeyboard(): void {
  const root = document.documentElement;
  root.style.setProperty('--cap-kb-height', '0px');

  let keyboardHeight = 0;

  import('@capacitor/keyboard')
    .then(({ Keyboard }) => {
      Keyboard.addListener('keyboardWillShow', (info) => {
        keyboardHeight = info.keyboardHeight;
        root.classList.add('cap-keyboard-open');
        root.style.setProperty('--cap-kb-height', `${keyboardHeight}px`);
        const active = document.activeElement as HTMLElement | null;
        if (active && isInputLike(active) && !isInsideOverlay(active)) {
          scrollFocusedIntoView();
        }
      });

      Keyboard.addListener('keyboardWillHide', () => {
        keyboardHeight = 0;
        root.classList.remove('cap-keyboard-open');
        root.style.setProperty('--cap-kb-height', '0px');
      });

      console.log('[Capacitor] iOS Keyboard listeners attached');
    })
    .catch(() => {
      initVisualViewportFallback(root);
    });

  function scrollFocusedIntoView() {
    requestAnimationFrame(() => {
      const el = document.activeElement as HTMLElement | null;
      if (!el || !isInputLike(el)) return;
      setTimeout(() => {
        el.scrollIntoView({ block: 'center' });
      }, 50);
    });
  }

  document.addEventListener(
    'focusin',
    (e) => {
      const target = e.target as HTMLElement;
      if (keyboardHeight > 0 && isInputLike(target) && !isInsideOverlay(target)) {
        scrollFocusedIntoView();
      }
    },
    { passive: true },
  );
}

// ── Android Implementation ─────────────────────────────────────────────

function initAndroidKeyboard(): void {
  const root = document.documentElement;
  root.style.setProperty('--cap-kb-height', '0px');

  let keyboardOpen = false;
  let keyboardHeight = 0;
  let lastFocused: HTMLElement | null = null;
  let ticking = false;

  const vv = window.visualViewport;

  function handleResize() {
    if (!vv) return;
    const heightDiff = window.innerHeight - vv.height;
    const wasOpen = keyboardOpen;

    if (heightDiff > 100) {
      keyboardOpen = true;
      keyboardHeight = heightDiff;
      root.classList.add('cap-keyboard-open');
      root.style.setProperty('--cap-kb-height', `${keyboardHeight}px`);
    } else {
      keyboardOpen = false;
      keyboardHeight = 0;
      root.classList.remove('cap-keyboard-open');
      root.style.setProperty('--cap-kb-height', '0px');
    }

    // Scroll focused element when keyboard just opened
    if (!wasOpen && keyboardOpen && lastFocused && isInputLike(lastFocused)) {
      smartScroll(lastFocused, keyboardHeight);
    }
  }

  function throttledResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      handleResize();
      ticking = false;
    });
  }

  // Attach viewport listeners
  if (vv) {
    vv.addEventListener('resize', throttledResize, { passive: true });
    vv.addEventListener('scroll', throttledResize, { passive: true });
    console.log('[Capacitor] Android keyboard: visualViewport listeners attached');
  } else {
    // Fallback for older WebViews
    window.addEventListener('resize', throttledResize);
    console.log('[Capacitor] Android keyboard: window.resize fallback attached');
  }

  // Focus-in listener for smart scroll
  document.addEventListener(
    'focusin',
    (e) => {
      const target = e.target as HTMLElement;
      if (!isInputLike(target)) return;
      if (target === lastFocused) return; // skip re-focus of same element
      lastFocused = target;

      // Wait for keyboard animation to complete
      setTimeout(() => {
        if (keyboardOpen) {
          smartScroll(target, keyboardHeight);
        }
      }, 300);
    },
    { passive: true },
  );

  // Clear last focused on blur
  document.addEventListener(
    'focusout',
    () => {
      lastFocused = null;
    },
    { passive: true },
  );
}

/**
 * Only scroll if the element's bottom edge is below the visible area.
 * Prevents unnecessary scroll jitter.
 */
function smartScroll(el: HTMLElement, kbHeight: number = 0): void {
  const visibleHeight = window.innerHeight - kbHeight;
  const rect = el.getBoundingClientRect();

  // Element is already fully visible — don't scroll
  if (rect.bottom < visibleHeight - 40 && rect.top > 60) return;

  // Use 'auto' instead of 'smooth' to prevent animation conflicts with keyboard
  el.scrollIntoView({ behavior: 'auto', block: 'center' });
}

// ── Shared Helpers ─────────────────────────────────────────────────────

function isInputLike(el: HTMLElement | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
}

function isInsideOverlay(el: HTMLElement): boolean {
  return !!el.closest('[data-overlay-type]');
}

function initVisualViewportFallback(root: HTMLElement) {
  const vv = window.visualViewport;
  if (!vv) return;

  const handleResize = () => {
    const diff = window.innerHeight - vv.height;
    const isOpen = diff > 150;

    if (isOpen) {
      root.classList.add('cap-keyboard-open');
      root.style.setProperty('--cap-kb-height', `${diff}px`);
    } else {
      root.classList.remove('cap-keyboard-open');
      root.style.setProperty('--cap-kb-height', '0px');
    }
  };

  vv.addEventListener('resize', handleResize);
  vv.addEventListener('scroll', handleResize);
}
